import Checkbox from 'components/Form/Checkbox';
import FieldLabel from 'components/Form/Field/FieldLabel';
import InputEmail from 'components/Form/InputEmail';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/Icon';
import SideBySide from 'components/Layout/SideBySide';
import Link from 'components/Link';
import ModalDestructiveConfirm from 'components/ModalDestructiveConfirm';
import SectionHeader from 'components/SectionHeader';
import TooltipWrapper from 'components/Tooltip';
import InboxLayout from 'pages/Portal/components/Inbox/InboxLayout';
import InboxTableLayout from 'pages/Portal/components/Inbox/TableLayout';
import {PortalPermissions} from 'pages/Portal/components/Inbox/types';
import Stroke from 'pages/Portal/components/Stroke';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {useLocale} from 'state/locale';
import {jt, t} from 'ttag';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';
import isEmail from 'validator/lib/isEmail';

import useEditClientInboxUsers from './services';
import {UserFormFields} from './types';

export type ClientInboxUsersEditBackend =
    Backend<ClientInboxUsersEditBackendData>;

type ClientInboxUsersEditBackendData = {
    clientName: string;
    clientUsers: string[];
    email: string;
    maxUsers: number;
    partnerName: string;
    permissions: PortalPermissions;
    portalMid: string;
    sessionExpiration: number;
};

type UserFormData = FormFields<UserFormFields>;

const cleanFormData = (data: UserFormData): UserFormData => {
    const cleaned = {...data};
    cleaned.newUsers = cleaned.newUsers.filter(
        (user: string): boolean => user.length > 0,
    );

    return cleaned;
};

const ClientInboxUsersEdit: FC = () => {
    const backendData = useBackend<ClientInboxUsersEditBackend>();
    const {
        clientName,
        clientUsers,
        email,
        maxUsers,
        partnerName,
        permissions,
        portalMid,
        sessionExpiration,
    } = backendData.data;
    const originalUsers: string[] = [...clientUsers];

    const methods = useForm<UserFormData>({
        defaultValues: {
            newUsers: originalUsers,
            originalUsers,
        },
    });
    const {getValues, watch} = methods;
    watch('newUsers');

    const {mutateAsync} = useEditClientInboxUsers();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    const hasAtLeastOneEmailSet = () =>
        cleanFormData(getValues()).newUsers.length > 0;

    const isFormValid = () => hasAtLeastOneEmailSet() && isChecked;

    const getTooltipMessage = () => {
        if (!hasAtLeastOneEmailSet()) {
            return t`Input at least one email address`;
        }
        if (!isChecked) {
            return t`You must agree to the MakeLeaps Corp. Privacy Policy.`;
        }
        return '';
    };

    const onSubmit = (formData: UserFormFields, isModalSubmit: boolean) => {
        const cleanedData = cleanFormData(formData);
        const hasPortalEmail = cleanedData.newUsers.includes(email);

        if (!hasPortalEmail && !isModalSubmit) {
            setIsConfirmModalOpen(true);
        } else {
            mutateAsync({formData: cleanedData, portalMid})
                .then(
                    onApiSuccess({
                        onSuccess: () =>
                            setUrl(
                                isModalSubmit
                                    ? '/portal/logout'
                                    : `/portal/${portalMid}/client-inbox/users/`,
                            ),
                    }),
                )
                .catch(onApiError(methods));
        }
    };

    const tooltipProps = {
        message: t`The email addresses which receive activity notifications and can access the Client Inbox`,
    };

    const fieldLabel = (
        <FieldLabel isSideBySide={true} tooltip={tooltipProps}>
            {t`Client Inbox Users`}
        </FieldLabel>
    );

    const locale = useLocale();

    const privacyLink =
        locale === 'en'
            ? 'https://www.makeleaps.com/en/about-us/'
            : 'https://www.makeleaps.jp/about/';

    const linkMlCorp = (
        <Link href={privacyLink} isOpenInNewTab={true} key={0}>
            {t`MakeLeaps Corp.`}
        </Link>
    );

    const linkAgreement = (
        <Link
            href="https://www.makeleaps.jp/%e3%83%97%e3%83%a9%e3%82%a4%e3%83%90%e3%82%b7%e3%83%bc%e3%83%9d%e3%83%aa%e3%82%b7%e3%83%bc/"
            isOpenInNewTab={true}
            key={0}
        >
            {t`Privacy Policy of MakeLeaps Corp.`}
        </Link>
    );

    const immediately = t`immediately`;

    return (
        <InboxLayout
            clientName={clientName}
            email={email}
            flags={backendData.flags}
            navigation="client-inbox-users"
            partnerName={partnerName}
            permissions={permissions}
            portalMid={portalMid}
            sessionExpiration={sessionExpiration}
        >
            <SectionHeader
                level={1}
                subtitle={t`Settings for this Portal, provided by ${partnerName}. Some settings may be editable if the provider has given you permission.`}
                title={t`Settings`}
            />
            <InboxTableLayout>
                <FormProvider {...methods}>
                    <form
                        onSubmit={methods.handleSubmit(() => {
                            onSubmit(methods.getValues(), false);
                        })}
                    >
                        <div className="flex w-full">
                            <div className="flex">
                                <Stroke />
                                <div className="min-w-0 whitespace-break-spaces break-words text-base font-semibold text-grey-800">
                                    {t`Client Inbox`}
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 flex w-[62rem] flex-col">
                            <SideBySide className="max-w-lg" spacing="sm">
                                {[...Array(maxUsers).keys()].map((index) => (
                                    <InputEmail
                                        className="mb-0"
                                        key={`user-email-input-${index}`}
                                        label={index === 0 ? fieldLabel : ''}
                                        name={`newUsers.${index}`}
                                        placeholder="example@domain.com"
                                        rules={{
                                            required: false,
                                            validate: (value: string) => {
                                                if (value) {
                                                    if (!isEmail(value)) {
                                                        return t`Please enter a valid email`;
                                                    }

                                                    if (
                                                        cleanFormData(
                                                            getValues(),
                                                        ).newUsers.filter(
                                                            (newEmail) =>
                                                                newEmail ===
                                                                value,
                                                        ).length > 1
                                                    ) {
                                                        return t`Each email address can only be used once.`;
                                                    }
                                                }

                                                return undefined;
                                            },
                                        }}
                                    />
                                ))}
                            </SideBySide>
                        </div>
                        <div className="flex w-[62rem] flex-col">
                            <p className="mt-6 text-sm text-light-secondary">
                                {jt`The email address(es) submitted here are provided to the contracting party of this Business Portal (${partnerName}) and are subject to use by ${linkMlCorp} per its Privacy Policy. Management of personal information at MakeLeaps Corp. is the responsibility of the Chief Executive Officer.`}
                            </p>
                            <div className="mt-4">
                                <Checkbox
                                    aria-label="agree"
                                    label={jt`I agree to the ${linkAgreement} (Japanese only).`}
                                    name="agree"
                                    onChange={(e) =>
                                        setIsChecked(e.target.checked)
                                    }
                                />
                            </div>
                            <div className="mt-5">
                                <ModalDestructiveConfirm
                                    header={
                                        <div className="flex">
                                            <Icon
                                                className="mr-2.5 inline-block text-coral-500"
                                                icon="warning"
                                                size="lg"
                                            />
                                            <span className="inline-block">
                                                {t`Remove the logged-in User?`}
                                            </span>
                                        </div>
                                    }
                                    isOpen={isConfirmModalOpen}
                                    onCancel={() => {
                                        setIsConfirmModalOpen(false);
                                    }}
                                    onConfirm={() => {
                                        onSubmit(methods.getValues(), true);
                                    }}
                                    submit={t`Remove and Logout`}
                                >
                                    <p className="text-sm">
                                        {t`By removing the email address ${email}, `}
                                    </p>
                                    <ul className="my-6 text-sm">
                                        <li>
                                            {t`it will no longer receive notifications from and lose access to this Client Inbox.`}
                                        </li>
                                        <li>
                                            {t`this Client Inbox session will be logged out ${immediately}.`}
                                        </li>
                                    </ul>
                                    <p className="text-sm">
                                        {t`Are you sure you want to remove ${email}?`}
                                    </p>
                                </ModalDestructiveConfirm>
                                <TooltipWrapper message={getTooltipMessage()}>
                                    <SubmitButton
                                        aria-label="save"
                                        disabled={!isFormValid()}
                                        kind="primary"
                                        size="sm"
                                    >
                                        {t`Save`}
                                    </SubmitButton>
                                </TooltipWrapper>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </InboxTableLayout>
        </InboxLayout>
    );
};

export default ClientInboxUsersEdit;
